<template>
  <button :class="mergedClasses" :disabled="disabled" @click="handleClick">
    <slot name="icon" v-if="$slots.icon"></slot>
    <slot></slot>
    <slot name="iconRight" v-if="$slots.iconRight"></slot>
  </button>
</template>

<script>
  import { classMerge } from "@/utils/utils"

  export default {
    name: "JdsButton",
    props: {
      variant: {
        type: String,
        default: "primary",
        validator: (value) =>
          ["primary", "secondary", "tertiary"].includes(value),
      },
      status: {
        type: String,
        default: "",
        validator: (value) => ["error", "success", "warning"].includes(value),
      },
      size: {
        type: String,
        default: "medium",
        validator: (value) => ["small", "medium", "large"].includes(value),
      },
      fullWidth: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      iconOnly: {
        type: Boolean,
        default: false,
      },
      className: {
        type: String,
        default: "",
      },
    },
    computed: {
      mergedClasses() {
        return classMerge(
          this.commonClasses,
          this.variantClasses,
          this.statusClasses,
          this.disabledClasses,
          this.sizeClasses,
          this.fullWidthClasses,
          this.iconOnlyClasses,
          this.className,
        )
      },
      commonClasses() {
        return "fe-inline-flex fe-items-center fe-justify-center fe-relative fe-text-body-s-bold fe-text-center fe-no-underline fe-outline-none fe-cursor-pointer fe-border fe-border-solid fe-transition-colors fe-rounded-pill"
      },
      variantClasses() {
        const variantClassesMap = {
          primary:
            "fe-bg-primary-50 fe-border-transparent fe-text-primary-inverse lg:hover:fe-bg-primary-60 focus-visible:fe-bg-primary-60 focus-visible:fe-text-primary-inverse focus-visible:fe-shadow-primary",
          secondary:
            "fe-text-primary-60 fe-bg-transparent fe-border-primary-grey-40 lg:hover:fe-border-primary-60 focus-visible:fe-text-primary-60 focus-visible:fe-shadow-primary",
          tertiary:
            "fe-text-primary-60 fe-bg-transparent fe-border-none lg:hover:fe-bg-primary-20 focus-visible:fe-bg-primary-20 focus-visible:fe-text-primary-60 focus-visible:fe-shadow-primary",
        }

        return variantClassesMap[this.variant] || ""
      },
      statusClasses() {
        if (this.status) {
          const statusClassesMap = {
            error: {
              primary: `fe-bg-feedback-error-20 fe-text-feedback-error-80 lg:hover:fe-bg-feedback-error-50 active:fe-bg-feedback-error-80 active:fe-text-feedback-error-20 focus-visible:fe-bg-feedback-error-20 focus-visible:fe-shadow-error`,
              secondary: `fe-border-feedback-error-50 fe-text-feedback-error-80 lg:hover:fe-border-feedback-error-50 active:fe-bg-feedback-error-20 active:fe-text-feedback-error-80 focus-visible:fe-text-feedback-error-80 focus-visible:fe-shadow-error`,
              tertiary: `fe-text-feedback-error-80 lg:hover:fe-bg-feedback-error-20 active:fe-bg-feedback-error-80 active:fe-text-feedback-error-20 focus-visible:fe-bg-feedback-error-20 focus-visible:fe-shadow-error`,
            },
            success: {
              primary: `fe-bg-feedback-success-20 fe-text-feedback-success-80 lg:hover:fe-bg-feedback-success-50 active:fe-bg-feedback-success-80 active:fe-text-feedback-success-20 focus-visible:fe-bg-feedback-success-20 focus-visible:fe-shadow-success`,
              secondary: `fe-border-feedback-success-50 fe-text-feedback-success-80 lg:hover:fe-border-feedback-success-50 active:fe-bg-feedback-success-20 active:fe-text-feedback-success-80 focus-visible:fe-text-feedback-success-80 focus-visible:fe-shadow-success`,
              tertiary: `fe-text-feedback-success-80 lg:hover:fe-bg-feedback-success-20 active:fe-bg-feedback-success-80 active:fe-text-feedback-success-20 focus-visible:fe-bg-feedback-success-20 focus-visible:fe-shadow-success`,
            },
            warning: {
              primary: `fe-bg-feedback-warning-20 fe-text-feedback-warning-80 lg:hover:fe-bg-feedback-warning-50 active:fe-bg-feedback-warning-80 active:fe-text-feedback-warning-20 focus-visible:fe-bg-feedback-warning-20 focus-visible:fe-shadow-warning`,
              secondary: `fe-border-feedback-warning-50 fe-text-feedback-warning-80 lg:hover:fe-border-feedback-warning-50 active:fe-bg-feedback-warning-20 active:fe-text-feedback-warning-80 focus-visible:fe-text-feedback-warning-80 focus-visible:fe-shadow-warning`,
              tertiary: `fe-text-feedback-warning-80 lg:hover:fe-bg-feedback-warning-20 active:fe-bg-feedback-warning-80 active:fe-text-feedback-warning-20 focus-visible:fe-bg-feedback-warning-20 focus-visible:fe-shadow-warning`,
            },
          }

          return statusClassesMap[this.status]
            ? statusClassesMap[this.status][this.variant]
            : ""
        }

        return ""
      },
      disabledClasses() {
        if (this.disabled) {
          // We are making bg classes important here because fynd inline css is overriding our classes.
          const disabledClassesMap = {
            primary: "!fe-bg-primary-60 fe-text-primary-inverse",
            secondary: "!fe-bg-transparent fe-border-primary-grey-80",
            tertiary: "fe-text-primary-60 !fe-bg-transparent",
          }

          const defaultClass = "!fe-opacity-30"

          return disabledClassesMap[this.variant]
            ? `${defaultClass} ${disabledClassesMap[this.variant]}`
            : ""
        }

        return ""
      },
      sizeClasses() {
        const sizeMap = {
          small: "fe-border fe-min-h-[2em] fe-px-base fe-py-xxs",
          medium: "fe-px-base fe-py-s",
          large: "fe-px-m fe-py-base",
        }
        return sizeMap[this.size] || ""
      },
      fullWidthClasses() {
        if (this.fullWidth) {
          return "fe-min-w-full"
        }

        return ""
      },
      iconOnlyClasses() {
        if (this.iconOnly) {
          const sizeMap = {
            small: "fe-p-xs",
            medium: "fe-p-s",
            large: "fe-p-base",
          }
          return sizeMap[this.size] || ""
        }

        return ""
      },
    },
    methods: {
      handleClick(event) {
        if (!this.disabled) {
          this.$emit("click", event)
        }
      },
    },
  }
</script>
